/* global window, document */

import get from 'lodash/get';
import clone from 'lodash/clone';
import merge from 'lodash/merge';
import store from 'store';

import PlayerInjector from 'video-js-core/src/PlayerInjector';
import Encore from './Encore';
import VideoEmbed from './VideoEmbed';

/**
 * HTV version of the shared video player.
 */
export default class EncoreInjector extends PlayerInjector {
  constructor(settings) {
    super(settings);

    this.BU = 'HTV';
    this.events = [];
    this.urlList = [];

    this.settings = settings;
    this.loadedEmbeds = [];
    this.recommendations = [];
    this.autoplayCount = 0;
    this.firstEmbedSet = new Set();

    this.infiniteScrollLoader();
    this.triggerReadyEvent();
    this.registerDisposeHandler();
  }

  /**
   * Triggers global event 'lumiere:ready'.
   */
  triggerReadyEvent() {
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent('lumiere:ready', false, false, undefined);
    window.dispatchEvent(evt);
  }

  /**
   * Overridden player injector.
   * @param {HTMLElement} el
   * @param {Object} playerSettings
   */
  loadPlayer(el, playerSettings) {
    const player = new Encore(el, clone(playerSettings));
    player.load();
    return player;
  }

  /**
   * Helper method to pause all player instances.
   * @param {string} ignoreInstanceId optional player ID to be ignored and not paused
   */
  pauseAllInstances(ignoreInstanceId) {
    this.instances.forEach(instance => {
      const { player } = instance;
      const { playerElementId } = instance;
      if (playerElementId !== ignoreInstanceId) {
        if (get(player, 'ima.controller.sdkImpl.adsActive', false)) {
          player.ima.pauseAd();
        } else {
          player.pause();
        }
      }
    });
  }

  /**
   * Helper method to find the current sticky player.
   * @return {instance} Encore video player instance.
   */
  getStickyInstance() {
    for (let i = 0; i < this.instances.length; i++) {
      if (this.instances[i].source.isSticky) {
        return this.instances[i];
      }
    }
    return null;
  }

  /**
   * Infinite scroll loader.
   * Periodically checks for yet-to-be-loaded video elements and initializes them.
   */
  infiniteScrollLoader() {
    const url = document.URL;
    if (!this.urlList.includes(url)) {
      this.urlList.push(url);
      this.initPlayerElements();
    }
    setTimeout(this.infiniteScrollLoader.bind(this), 1000);
  }

  /**
   * Recommendations loader.
   * Checks the global recommendations object and fetches video embed data.
   * Store a list of loaded embeds, so that only new embed data requested.
   * If loading fails, reset the loaded embeds list.
   */
  loadRecommendations() {
    if (!get(window, 'hearstLumiereRecs.0.embeds')) {
      return;
    }

    const globalRecs = clone(get(window, 'hearstLumiereRecs.0.embeds'));

    if (store.enabled) {
      this.loadedEmbeds = merge(this.loadedEmbeds, store.get('hearstLumiereWatched', []));
    }

    const embedsToLoad = globalRecs.filter(item => {
      if (!this.loadedEmbeds.includes(item)) {
        this.loadedEmbeds.push(item);
        return true;
      }
      return false;
    });

    const embedPromises = embedsToLoad.map(item => {
      const videoEmbed = new VideoEmbed(item, this.settings);
      return videoEmbed.load();
    });

    Promise.all(embedPromises)
      .then(result => {
        this.recommendations = merge(this.recommendations, result);
      })
      .catch(() => {
        this.loadedEmbeds = [];
      });
  }

  /**
   * Increments first video embed count.
   */
  incFirstVideoEmbed(id) {
    if (this.firstEmbedSet.size === 0) {
      this.firstEmbedSet.add(id);
    }
  }

  /**
   * Returns whether or not a player ID is the first video embed.
   */
  isFirstVideoEmbed(id) {
    return this.firstEmbedSet.has(id);
  }

  /**
   * Increments global autoplay count.
   */
  incAutoplayCount() {
    this.autoplayCount++;
  }

  /**
   * Resets global autoplay count.
   */
  resetAutoplayCount() {
    this.autoplayCount = 0;
  }

  /**
   * Registers a 'beforeunload' handler to dispose of all player on page unload.
   */
  registerDisposeHandler() {
    window.addEventListener('beforeunload', () => {
      this.instances.forEach(instance => {
        const { player } = instance;
        player.dispose();
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/EncoreInjector.js